import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import { formatPassengerName } from '@/lib/passengerName';
import {
  Loader2,
  RefreshCw,
  Zap,
  Eye,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Pencil,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Link2,
  CreditCard,
  UserCircle,
  Settings,
  Users,
  ArrowUpRight,
  Accessibility,
  MapPinned,
  ClipboardList,
  AlertTriangle,
  ShieldCheck,
  Home,
  Hash,
  Plus,
  Trash2,
  X,
  CalendarClock,
  PhoneCall,
  PhoneForwarded,
  Car,
  Check,
} from 'lucide-react';

import type { CRMAccount, CRMFormFocus } from './CRMAccountForm';
import CRMBookableAddresses from './CRMBookableAddresses';
import CRMAccountApprovals from './CRMAccountApprovals';
import CRMAccountPermanentBookings from './CRMAccountPermanentBookings';
import CRMLinkedRecordsPanel from './CRMLinkedRecordsPanel';
import { OverviewTile, TileSection, type TileTone } from './CRMOverviewTile';


interface TaxiCallerJob {
  id: string;
  Job_ID: string | null;
  pu_date: string | null;
  pu_time: string | null;
  Passenger_First_Name: string | null;
  Passenger_Last_Name: string | null;
  Pick_Up_Location: string | null;
  Drop_Off_Location: string | null;
  Project: string | null;
  Cost_Code: string | null;
  Reference: string | null;
  fleet: string | null;
  driver_name: string | null;
  vehicle_number: string | null;
}

interface CurrentApprovalSummary {
  id: string;
  account_id: string;
  approval_type: string | null;
  source_provider: string | null;
  claim_number: string | null;
  purchase_order_number: string | null;
  service_code: string | null;
  project: string | null;
  cost_code: string | null;
  reference: string | null;
  approval_from: string | null;
  approval_to: string | null;
  approval_is_open: boolean;
  approval_status: string | null;
  days_to_expiry: number | null;
  trip_frequency: string | null;
  trip_count: number | null;
  is_current: boolean;
}

interface CRMAccountDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CRMAccount | null;
  /** `focus` opens the edit form on a specific tab/section (used by overview tiles). */
  onEdit: (account: CRMAccount, focus?: CRMFormFocus) => void;
  onCreateSubAccount?: (
    parentId: string,
    parentName: string,
    mode: 'sub-account' | 'traveller' | 'ooa'
  ) => void;
  onDeleted?: () => void;
  /** Called after the header Active/Inactive badge changes the client's status. */
  onActiveChanged?: (accountId: string, isActive: boolean) => void;
}

const JOBS_PER_PAGE = 25;

const formatTripAllowance = (frequency?: string | null, count?: number | null) => {
  if (!frequency || frequency === 'open') return 'Open / not specified';
  if (!count || count <= 0) {
    if (frequency === 'one_off') return 'One-off';
    if (frequency === 'weekly') return 'Per week';
    if (frequency === 'monthly') return 'Per month';
    return '-';
  }
  if (frequency === 'one_off') return `${count} ${count === 1 ? 'trip' : 'trips'} one-off`;
  if (frequency === 'weekly') return `${count} ${count === 1 ? 'trip' : 'trips'} / week`;
  if (frequency === 'monthly') return `${count} ${count === 1 ? 'trip' : 'trips'} / month`;
  return '-';
};

const getApprovalBadgeClass = (status?: string | null) => {
  switch (status) {
    case 'Current':
      return 'bg-green-900/50 text-green-400 border-green-700';
    case 'Active':
      return 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50';
    case 'Open':
      return 'bg-blue-900/40 text-blue-400 border-blue-700/50';
    case 'Upcoming':
      return 'bg-yellow-900/40 text-yellow-400 border-yellow-700/50';
    case 'Expired':
      return 'bg-red-900/30 text-red-400 border-red-700/50';
    case 'Inactive':
      return 'bg-secondary text-muted-foreground border-border';
    default:
      return 'bg-secondary text-muted-foreground border-border';
  }
};

const PREFERRED_VEHICLE_LABELS: Record<string, string> = {
  sedan: 'Sedan',
  suv_van: 'SUV/Van',
  wheelchair_van: 'Wheelchair Van',
};

const CRMAccountDetail: React.FC<CRMAccountDetailProps> = ({
  open,
  onOpenChange,
  account,
  onEdit,
  onCreateSubAccount,
  onDeleted,
  onActiveChanged,
}) => {
  const [jobs, setJobs] = useState<TaxiCallerJob[]>([]);
  const [jobCount, setJobCount] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobPage, setJobPage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [jobDateFilter, setJobDateFilter] = useState<'all' | 'day' | 'week' | 'month'>('all');

  const [backfilling, setBackfilling] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [parentAccount, setParentAccount] = useState<{ account_id: string; account_name: string } | null>(null);
  const [childAccounts, setChildAccounts] = useState<CRMAccount[]>([]);
  const [billingAccount, setBillingAccount] = useState<{ account_id: string; account_name: string } | null>(null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  const [overviewAddresses, setOverviewAddresses] = useState<any[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressesExpanded, setAddressesExpanded] = useState(false);

  const [overviewQuickFilter, setOverviewQuickFilter] = useState<'all' | 'day' | 'week' | 'month'>('week');
  const [overviewJobCount, setOverviewJobCount] = useState(0);
  const [recentJobIds, setRecentJobIds] = useState<{ Job_ID: string; pu_date: string }[]>([]);
  const [loadingOverviewJobs, setLoadingOverviewJobs] = useState(false);
  const [jobsExpanded, setJobsExpanded] = useState(false);

  const [currentApproval, setCurrentApproval] = useState<CurrentApprovalSummary | null>(null);
  const [loadingCurrentApproval, setLoadingCurrentApproval] = useState(false);
  const [permSummary, setPermSummary] = useState<{ count: number; nextExpiry: string | null; days: number | null } | null>(
    null
  );
  // Set once the header Active badge has been clicked, so the panel shows the
  // new state without waiting for the parent to hand back a fresh account.
  const [activeOverride, setActiveOverride] = useState<boolean | null>(null);
  const [savingActive, setSavingActive] = useState(false);

  useEffect(() => {
    setActiveOverride(null);
  }, [account?.id, account?.is_active, open]);

  const getDateFilterRange = useCallback((filter: 'all' | 'day' | 'week' | 'month'): string | null => {
    if (filter === 'all') return null;
    const now = new Date();
    if (filter === 'day') return now.toISOString().split('T')[0];

    if (filter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return weekAgo.toISOString().split('T')[0];
    }

    if (filter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return monthAgo.toISOString().split('T')[0];
    }

    return null;
  }, []);

  const loadCurrentApproval = useCallback(async () => {
    if (!account) return;
    setLoadingCurrentApproval(true);

    try {
      const { data, error } = await supabase
        .from('crm_account_approvals_with_status')
        .select('*')
        .eq('account_id', account.account_id)
        .eq('is_current', true)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setCurrentApproval((data as CurrentApprovalSummary) || null);
    } catch (err) {
      console.error('Error loading current approval:', err);
      setCurrentApproval(null);
    } finally {
      setLoadingCurrentApproval(false);
    }
  }, [account]);

  const loadJobs = useCallback(async () => {
    if (!account) return;
    setLoadingJobs(true);

    try {
      const dateFrom = getDateFilterRange(jobDateFilter);

      let countQuery = supabase
        .from('taxicaller_data')
        .select('id', { count: 'exact', head: true })
        .eq('Account_ID', account.account_id);

      if (dateFrom) countQuery = countQuery.gte('pu_date', dateFrom);

      const { count } = await countQuery;
      setJobCount(count || 0);

      const from = jobPage * JOBS_PER_PAGE;
      const to = from + JOBS_PER_PAGE - 1;

      let dataQuery = supabase
        .from('taxicaller_data')
        .select(
          'id, "Job_ID", pu_date, pu_time, "Passenger_First_Name", "Passenger_Last_Name", "Pick_Up_Location", "Drop_Off_Location", "Project", "Cost_Code", "Reference", fleet, driver_name, vehicle_number'
        )
        .eq('Account_ID', account.account_id);

      if (dateFrom) dataQuery = dataQuery.gte('pu_date', dateFrom);

      dataQuery = dataQuery
        .order('pu_date', { ascending: false, nullsFirst: false })
        .order('pu_time', { ascending: false, nullsFirst: false })
        .range(from, to);

      const { data, error } = await dataQuery;
      if (error) throw error;

      setJobs(data || []);
    } catch (err: any) {
      console.error('Error loading jobs:', err);
      toast({
        title: 'Error',
        description: 'Failed to load linked jobs',
        variant: 'destructive',
      });
    } finally {
      setLoadingJobs(false);
    }
  }, [account, jobPage, jobDateFilter, getDateFilterRange]);

  const loadHierarchy = useCallback(async () => {
    if (!account) return;
    setLoadingHierarchy(true);

    try {
      if (account.parent_account_id) {
        const { data } = await supabase
          .from('crm_accounts')
          .select('account_id, account_name')
          .eq('account_id', account.parent_account_id)
          .single();
        setParentAccount(data || null);
      } else {
        setParentAccount(null);
      }

      if (account.billing_account_id) {
        const { data } = await supabase
          .from('crm_accounts')
          .select('account_id, account_name')
          .eq('account_id', account.billing_account_id)
          .single();
        setBillingAccount(data || null);
      } else {
        setBillingAccount(null);
      }

      const { data: children } = await supabase
        .from('crm_accounts')
        .select('*')
        .eq('parent_account_id', account.account_id)
        .order('account_name', { ascending: true });

      setChildAccounts(children || []);
    } catch (err) {
      console.error('Error loading hierarchy:', err);
    } finally {
      setLoadingHierarchy(false);
    }
  }, [account]);

  const loadOverviewAddresses = useCallback(async () => {
    if (!account) return;
    setLoadingAddresses(true);

    try {
      const { data, error } = await supabase
        .from('crm_account_addresses')
        .select('*')
        .eq('account_id', account.account_id)
        .order('address_type', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const sorted = (data || []).sort((a: any, b: any) => {
        const aIsHome = a.address_type === 'home' ? 0 : 1;
        const bIsHome = b.address_type === 'home' ? 0 : 1;
        return aIsHome - bIsHome;
      });

      setOverviewAddresses(sorted);
    } catch (err) {
      console.error('Error loading overview addresses:', err);
    } finally {
      setLoadingAddresses(false);
    }
  }, [account]);

  const loadPermSummary = useCallback(async () => {
    if (!account) return;

    try {
      const { data, error } = await supabase
        .from('crm_permanent_booking_summary')
        .select('active_perm_booking_count, next_perm_expiry, next_perm_days_remaining')
        .eq('account_id', account.account_id)
        .maybeSingle();

      if (error) throw error;

      setPermSummary({
        count: Number(data?.active_perm_booking_count) || 0,
        nextExpiry: data?.next_perm_expiry ?? null,
        days: data?.next_perm_days_remaining ?? null,
      });
    } catch (err) {
      console.error('Error loading permanent booking summary:', err);
      setPermSummary(null);
    }
  }, [account]);

  const loadOverviewJobs = useCallback(async () => {
    if (!account) return;
    setLoadingOverviewJobs(true);

    try {
      const dateFrom = getDateFilterRange(overviewQuickFilter);

      let countQ = supabase
        .from('taxicaller_data')
        .select('id', { count: 'exact', head: true })
        .eq('Account_ID', account.account_id);

      if (dateFrom) countQ = countQ.gte('pu_date', dateFrom);

      const { count } = await countQ;
      setOverviewJobCount(count || 0);

      let recentQ = supabase
        .from('taxicaller_data')
        .select('"Job_ID", pu_date')
        .eq('Account_ID', account.account_id);

      if (dateFrom) recentQ = recentQ.gte('pu_date', dateFrom);

      recentQ = recentQ
        .order('pu_date', { ascending: false, nullsFirst: false })
        .order('pu_time', { ascending: false, nullsFirst: false })
        .limit(10);

      const { data } = await recentQ;
      setRecentJobIds((data || []).filter((r: any) => r.Job_ID) as any);
    } catch (err) {
      console.error('Error loading overview jobs:', err);
    } finally {
      setLoadingOverviewJobs(false);
    }
  }, [account, overviewQuickFilter, getDateFilterRange]);

  const reloadAccountShellData = useCallback(async () => {
    if (!account) return;

    try {
      const { data, error } = await supabase
        .from('crm_accounts')
        .select('*')
        .eq('id', account.id)
        .single();

      if (error) throw error;

      Object.assign(account, data || {});
      await loadCurrentApproval();
      await loadJobs();
      await loadOverviewJobs();
    } catch (err) {
      console.error('Error refreshing account shell data:', err);
    }
  }, [account, loadCurrentApproval, loadJobs, loadOverviewJobs]);

  useEffect(() => {
    if (open && account) {
      setJobPage(0);
      setActiveTab('overview');
      setAddressesExpanded(false);
      setJobsExpanded(false);
      setOverviewQuickFilter('week');
    }
  }, [open, account?.id]);

  useEffect(() => {
    if (open && account) {
      loadCurrentApproval();
      loadJobs();
      loadHierarchy();
      loadOverviewAddresses();
      loadOverviewJobs();
      loadPermSummary();
    }
  }, [open, account, loadCurrentApproval, loadJobs, loadHierarchy, loadOverviewAddresses, loadOverviewJobs, loadPermSummary]);

  // The overview tiles summarise things edited on other tabs (addresses,
  // approvals, permanent bookings) -- re-read them each time you come back.
  useEffect(() => {
    if (open && account && activeTab === 'overview') {
      loadOverviewAddresses();
      loadCurrentApproval();
      loadPermSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleBackfill = async () => {
    if (!account) return;
    setBackfilling(true);

    try {
      const { data, error } = await supabase.functions.invoke('crm-backfill', {
        body: {
          account_id: account.account_id,
          default_project: account.default_project,
          default_cost_code: account.default_cost_code,
          default_reference: account.default_reference,
          preview_only: false,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const u = data.updated || {};
      toast({
        title: 'Backfill Complete',
        description: `Updated: ${u.project || 0} Project, ${u.cost_code || 0} Cost Code, ${u.reference || 0} Reference fields.`,
      });

      loadJobs();
    } catch (err: any) {
      toast({
        title: 'Backfill Failed',
        description: err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBackfilling(false);
    }
  };

  const handlePreview = async () => {
    if (!account) return;
    setPreviewing(true);

    try {
      const { data, error } = await supabase.functions.invoke('crm-backfill', {
        body: {
          account_id: account.account_id,
          default_project: account.default_project,
          default_cost_code: account.default_cost_code,
          default_reference: account.default_reference,
          preview_only: true,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setPreviewData(data);
      setShowPreview(true);
    } catch (err: any) {
      toast({
        title: 'Preview Failed',
        description: err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!account) return;
    setDeleting(true);

    try {
      await supabase.from('crm_account_addresses').delete().eq('account_id', account.account_id);

      const { error } = await supabase.from('crm_accounts').delete().eq('id', account.id);
      if (error) throw error;

      toast({
        title: 'Account Deleted',
        description: `${account.account_name} (${account.account_id}) has been permanently deleted.`,
      });

      setShowDeleteConfirm(false);
      onOpenChange(false);
      onDeleted?.();
    } catch (err: any) {
      console.error('Delete error:', err);
      toast({
        title: 'Delete Failed',
        description: err.message || 'Failed to delete account',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const isActive = activeOverride ?? account?.is_active ?? true;

  const handleToggleActive = async () => {
    if (!account) return;
    const next = !isActive;
    if (
      !next &&
      !window.confirm(`Mark ${account.account_name} (${account.account_id}) as inactive?`)
    ) {
      return;
    }

    setSavingActive(true);
    try {
      const { error } = await supabase
        .from('crm_accounts')
        .update({ is_active: next, updated_at: new Date().toISOString() })
        .eq('id', account.id);
      if (error) throw error;

      setActiveOverride(next);
      onActiveChanged?.(account.account_id, next);
      toast({
        title: next ? 'Account Activated' : 'Account Deactivated',
        description: `${account.account_name} is now ${next ? 'active' : 'inactive'}.`,
      });
    } catch (err: any) {
      console.error('Error toggling account active state:', err);
      toast({
        title: 'Could not update account',
        description: err.message || 'Failed to change active status',
        variant: 'destructive',
      });
    } finally {
      setSavingActive(false);
    }
  };

  const isNonBilled =
    account?.billed_status === 'Non-Billed' || (!account?.billed_status && !account?.account_series_type);
  const canDelete = isNonBilled;

  const totalPages = Math.ceil(jobCount / JOBS_PER_PAGE);

  if (!account) return null;

  const DetailRow = ({
    label,
    value,
    icon: Icon,
  }: {
    label: string;
    value: string | null | undefined;
    icon?: any;
  }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-2 text-sm">
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />}
        <div>
          <span className="text-muted-foreground text-xs block">{label}</span>
          <span className="text-foreground">{value}</span>
        </div>
      </div>
    );
  };

  const travellers = childAccounts.filter(
    (c) => c.hierarchy_type === 'Traveller' || c.account_category === 'Traveller'
  );
  const subAccounts = childAccounts.filter(
    (c) => c.hierarchy_type !== 'Traveller' && c.account_category !== 'Traveller'
  );

  // ---- values behind the overview tiles ----
  const addressText = (a: any): string =>
    a.full_address || [a.address_line_1, a.address_line_2, a.suburb, a.city, a.postcode].filter(Boolean).join(', ');
  const activeAddresses = overviewAddresses.filter((a: any) => a.is_active);
  const homeAddress = activeAddresses.find((a: any) => a.address_type === 'home') ?? null;
  const destinationAddresses = activeAddresses.filter((a: any) => a.address_type !== 'home');
  const preferredVehicleLabel = PREFERRED_VEHICLE_LABELS[account.preferred_vehicle || ''] ?? 'Not specified';
  const requirementLabels = [
    account.requires_project && 'Project',
    account.requires_reference && 'Reference',
    account.requires_cost_code && 'Cost code',
    account.requires_purchase_order && 'PO',
    account.allow_direct_settlement && 'Direct settlement',
  ].filter(Boolean) as string[];

  const approvalTone: TileTone = !currentApproval
    ? 'neutral'
    : currentApproval.approval_status === 'Expired'
    ? 'bad'
    : !currentApproval.approval_is_open && currentApproval.days_to_expiry !== null && currentApproval.days_to_expiry <= 30
    ? 'warn'
    : ['Current', 'Active', 'Open'].includes(currentApproval.approval_status || '')
    ? 'good'
    : 'neutral';
  const approvalHint = currentApproval
    ? [
        currentApproval.approval_is_open
          ? 'Open approval'
          : currentApproval.days_to_expiry === null
          ? null
          : currentApproval.days_to_expiry < 0
          ? `Expired ${Math.abs(currentApproval.days_to_expiry)}d ago`
          : `${currentApproval.days_to_expiry}d left`,
        currentApproval.project,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Nothing set up yet';

  const permTone: TileTone =
    !permSummary || permSummary.count === 0
      ? 'neutral'
      : permSummary.days !== null && permSummary.days < 0
      ? 'bad'
      : permSummary.days !== null && permSummary.days <= 30
      ? 'warn'
      : 'good';
  const permHint =
    permSummary && permSummary.count > 0
      ? permSummary.days === null || !permSummary.nextExpiry
        ? 'No expiry set'
        : permSummary.days < 0
        ? `Expired ${formatDate(permSummary.nextExpiry)} (${Math.abs(permSummary.days)}d ago)`
        : `Next expiry ${formatDate(permSummary.nextExpiry)} (${permSummary.days}d left)`
      : 'No permanent bookings';

  const editAt = (focus: CRMFormFocus) => {
    onOpenChange(false);
    onEdit(account, focus);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl w-[96vw] h-[92vh] p-0 bg-card border-border overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border px-6 py-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <DialogTitle className="golden-text text-2xl truncate">
                  {account.account_name}
                </DialogTitle>

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-muted-foreground text-sm font-mono">{account.account_id}</span>

                  {account.account_series_type && (
                    <Badge className="bg-blue-900/40 text-blue-400 border-blue-700/50 text-xs">
                      {account.account_series_type}
                    </Badge>
                  )}

                  {account.billed_status && (
                    <Badge
                      className={`text-xs ${
                        account.billed_status === 'Billed'
                          ? 'bg-green-900/40 text-green-400 border-green-700/50'
                          : 'bg-secondary text-muted-foreground border-border'
                      }`}
                    >
                      {account.billed_status}
                    </Badge>
                  )}

                  {account.account_category && (
                    <Badge variant="outline" className="text-xs">
                      {account.account_category}
                    </Badge>
                  )}

                  {account.hierarchy_type && (
                    <Badge className="bg-purple-900/40 text-purple-400 border-purple-700/50 text-xs">
                      {account.hierarchy_type}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleToggleActive}
                  disabled={savingActive}
                  title={isActive ? 'Click to mark this client inactive' : 'Click to mark this client active'}
                  className="disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/60 rounded-full"
                >
                  <Badge
                    variant={isActive ? 'default' : 'secondary'}
                    className={`cursor-pointer hover:opacity-80 transition-opacity ${
                      isActive
                        ? 'bg-green-900/50 text-green-400 border-green-700'
                        : 'bg-red-900/50 text-red-400 border-red-700'
                    }`}
                  >
                    {savingActive ? 'Saving...' : isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </button>

                {canDelete && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-600/60 text-red-400 hover:bg-red-900/30"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Delete
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                  onClick={() => {
                    onOpenChange(false);
                    onEdit(account);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" />
                  Edit
                </Button>

                <Button
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-4 flex-shrink-0">
              <TabsList className="w-full justify-start overflow-x-auto rounded-xl border border-border bg-secondary/40 p-1 h-auto">
                <TabsTrigger value="overview" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <Building2 className="w-4 h-4" />
                  Overview
                </TabsTrigger>

                <TabsTrigger value="hierarchy" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <Link2 className="w-4 h-4" />
                  Hierarchy
                  {childAccounts.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1">
                      {childAccounts.length}
                    </Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger value="billing" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <CreditCard className="w-4 h-4" />
                  Billing
                </TabsTrigger>

                <TabsTrigger value="traveller" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <UserCircle className="w-4 h-4" />
                  Traveller
                </TabsTrigger>

                <TabsTrigger value="addresses" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <MapPin className="w-4 h-4" />
                  Addresses
                  {activeAddresses.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1">
                      {activeAddresses.length}
                    </Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger value="approvals" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <ShieldCheck className="w-4 h-4" />
                  Approvals
                </TabsTrigger>

                <TabsTrigger value="perm-bookings" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <CalendarClock className="w-4 h-4" />
                  Perm Bookings
                </TabsTrigger>

                <TabsTrigger value="jobs" className="rounded-lg px-4 py-2 text-sm gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground">
                  <ClipboardList className="w-4 h-4" />
                  Jobs
                  <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1">
                    {jobCount}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <TabsContent value="overview" className="mt-0 space-y-6">
                <TileSection icon={Building2} title="Account Details" hint="Click a tile to update it">
                  <OverviewTile
                    icon={User}
                    title="Contact"
                    value={account.contact_name || 'No contact name'}
                    hint={[account.phone, account.email].filter(Boolean).join(' · ') || 'No phone or email on file'}
                    actionLabel="Edit contact"
                    onClick={() => editAt({ tab: 'basic' })}
                  />

                  <OverviewTile
                    icon={Building2}
                    title="Account Type"
                    tone={isActive ? 'neutral' : 'bad'}
                    value={[account.account_series_type, account.billed_status].filter(Boolean).join(' · ') || 'Not set'}
                    hint={
                      [account.account_category, account.hierarchy_type, isActive ? null : 'Inactive']
                        .filter(Boolean)
                        .join(' · ') || (isActive ? 'Active' : 'Inactive')
                    }
                    actionLabel="Edit account"
                    onClick={() => editAt({ tab: 'basic' })}
                  />

                  <OverviewTile
                    icon={CreditCard}
                    title="Billing"
                    value={account.billing_name || account.billed_status || 'Not set'}
                    hint={
                      [account.payment_terms, account.invoice_email || account.billing_email].filter(Boolean).join(' · ') ||
                      'No billing details yet'
                    }
                    actionLabel="Open billing"
                    onClick={() => setActiveTab('billing')}
                  />

                  <OverviewTile
                    icon={UserCircle}
                    title="Traveller"
                    value={
                      account.traveller_full_name ||
                      [account.traveller_first_name, account.traveller_last_name].filter(Boolean).join(' ') ||
                      'No traveller details'
                    }
                    hint={account.traveller_mobile || account.traveller_phone || account.traveller_email || undefined}
                    actionLabel="Open traveller"
                    onClick={() => setActiveTab('traveller')}
                  />

                  <OverviewTile
                    icon={Link2}
                    title="Hierarchy"
                    value={
                      parentAccount
                        ? `Under ${parentAccount.account_name}`
                        : childAccounts.length > 0
                        ? `${childAccounts.length} linked account${childAccounts.length === 1 ? '' : 's'}`
                        : 'Standalone account'
                    }
                    hint={
                      billingAccount
                        ? `Billed via ${billingAccount.account_name}`
                        : parentAccount && childAccounts.length > 0
                        ? `${childAccounts.length} linked account${childAccounts.length === 1 ? '' : 's'}`
                        : undefined
                    }
                    actionLabel="Open hierarchy"
                    onClick={() => setActiveTab('hierarchy')}
                  />

                  <OverviewTile
                    icon={FileText}
                    title="Account Notes"
                    value={account.notes || 'No notes'}
                    actionLabel="Edit notes"
                    onClick={() => editAt({ tab: 'basic' })}
                  />
                </TileSection>

                <TileSection icon={ShieldCheck} title="Approval Details">
                  <OverviewTile
                    icon={ShieldCheck}
                    title="Approval Status"
                    tone={approvalTone}
                    value={
                      loadingCurrentApproval
                        ? 'Loading...'
                        : currentApproval
                        ? currentApproval.approval_status || 'Unknown'
                        : 'No current approval'
                    }
                    hint={approvalHint}
                    actionLabel="Manage approvals"
                    onClick={() => setActiveTab('approvals')}
                  />

                  {currentApproval && (
                    <>
                      <OverviewTile
                        icon={CalendarClock}
                        title="Approval Period"
                        tone={approvalTone}
                        value={
                          currentApproval.approval_is_open
                            ? 'Open approval'
                            : `${formatDate(currentApproval.approval_from)} → ${formatDate(currentApproval.approval_to)}`
                        }
                        hint={
                          currentApproval.approval_is_open
                            ? 'No end date'
                            : currentApproval.days_to_expiry === null
                            ? undefined
                            : currentApproval.days_to_expiry < 0
                            ? `Expired ${Math.abs(currentApproval.days_to_expiry)}d ago`
                            : `${currentApproval.days_to_expiry}d remaining`
                        }
                        actionLabel="Manage approvals"
                        onClick={() => setActiveTab('approvals')}
                      />

                      <OverviewTile
                        icon={Hash}
                        title="Trip Allowance"
                        value={formatTripAllowance(currentApproval.trip_frequency, currentApproval.trip_count)}
                        actionLabel="Manage approvals"
                        onClick={() => setActiveTab('approvals')}
                      />

                      <OverviewTile
                        icon={FileText}
                        title="Claim / PO"
                        value={
                          [currentApproval.claim_number, currentApproval.purchase_order_number].filter(Boolean).join(' / ') ||
                          'None'
                        }
                        hint={currentApproval.source_provider ? `Claim manager: ${currentApproval.source_provider}` : undefined}
                        actionLabel="Manage approvals"
                        onClick={() => setActiveTab('approvals')}
                      />

                      <OverviewTile
                        icon={ClipboardList}
                        title="Project / Cost Code"
                        value={currentApproval.project || 'No project'}
                        hint={[currentApproval.cost_code, currentApproval.reference].filter(Boolean).join(' • ') || undefined}
                        actionLabel="Manage approvals"
                        onClick={() => setActiveTab('approvals')}
                      />
                    </>
                  )}

                  <OverviewTile
                    icon={CalendarClock}
                    title="Perm Bookings"
                    tone={permTone}
                    value={permSummary && permSummary.count > 0 ? `${permSummary.count} active` : 'None'}
                    hint={permHint}
                    actionLabel="Manage bookings"
                    onClick={() => setActiveTab('perm-bookings')}
                  />

                  {currentApproval && (
                    <>
                      <OverviewTile
                        icon={Eye}
                        title="Preview Affected Jobs"
                        value={previewing ? 'Checking...' : "See what a backfill would change"}
                        hint="Nothing is changed"
                        actionLabel="Run preview"
                        disabled={previewing}
                        onClick={handlePreview}
                      />

                      <OverviewTile
                        icon={Zap}
                        title="Backfill Blank Fields"
                        tone="warn"
                        value={backfilling ? 'Backfilling...' : 'Fill blank job fields from this approval'}
                        hint="Updates this account's existing jobs"
                        actionLabel="Run backfill"
                        disabled={backfilling}
                        onClick={() => {
                          if (window.confirm("Backfill blank fields on this account's jobs from the current approval?")) {
                            handleBackfill();
                          }
                        }}
                      />
                    </>
                  )}
                </TileSection>

                <TileSection icon={PhoneCall} title="IVR Details">
                  <OverviewTile
                    icon={PhoneCall}
                    title="Caller ID"
                    tone={account.is_fixed_location_number ? 'good' : 'neutral'}
                    value={account.is_fixed_location_number ? 'Fixed location number' : 'Normal caller'}
                    hint={
                      account.is_fixed_location_number
                        ? `${account.phone || 'No phone on file'} — IVR offers the approved pickup address`
                        : account.phone || 'No phone on file'
                    }
                    actionLabel="Edit IVR settings"
                    onClick={() => editAt({ tab: 'basic', section: 'ivr' })}
                  />

                  <OverviewTile
                    icon={PhoneForwarded}
                    title="Operator Divert"
                    tone={account.always_divert_to_operator ? 'warn' : 'neutral'}
                    value={account.always_divert_to_operator ? 'Always diverts to operator' : 'IVR books automatically'}
                    hint={account.always_divert_to_operator ? 'Every call from this account goes to a human' : 'Not diverted'}
                    actionLabel="Edit IVR settings"
                    onClick={() => editAt({ tab: 'basic', section: 'ivr' })}
                  />

                  <OverviewTile
                    icon={Car}
                    title="Preferred Vehicle"
                    tone={preferredVehicleLabel === 'Not specified' ? 'neutral' : 'info'}
                    value={preferredVehicleLabel}
                    hint="What the IVR sends TaxiCaller for their bookings"
                    actionLabel="Edit vehicle"
                    onClick={() => editAt({ tab: 'traveller', section: 'transport' })}
                  />
                </TileSection>

                <TileSection icon={MapPin} title="Address Details">
                  <OverviewTile
                    icon={ShieldCheck}
                    title="Address Approval"
                    tone={account.open_approval ? 'good' : destinationAddresses.length === 0 ? 'warn' : 'info'}
                    value={account.open_approval ? 'Open approval' : 'Approved addresses only'}
                    hint={
                      account.open_approval
                        ? 'Can travel to any destination'
                        : destinationAddresses.length === 0
                        ? 'No approved destinations added yet'
                        : `${destinationAddresses.length} approved destination${destinationAddresses.length === 1 ? '' : 's'}`
                    }
                    actionLabel="Edit approval & addresses"
                    onClick={() => editAt({ tab: 'basic', section: 'addresses' })}
                  />

                  <OverviewTile
                    icon={Home}
                    title="Home Address"
                    tone={loadingAddresses ? 'neutral' : homeAddress ? 'good' : 'bad'}
                    value={
                      loadingAddresses ? (
                        'Loading...'
                      ) : homeAddress ? (
                        <span className="flex items-center gap-1.5">
                          <Check className="w-4 h-4 text-green-400 shrink-0" />
                          Home address on file
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <X className="w-4 h-4 text-red-400 shrink-0" />
                          No home address
                        </span>
                      )
                    }
                    hint={homeAddress ? addressText(homeAddress) : 'Add one so the IVR can offer it'}
                    actionLabel="Open addresses"
                    onClick={() => setActiveTab('addresses')}
                  />

                  <OverviewTile
                    icon={MapPin}
                    title="Saved Destinations"
                    tone={destinationAddresses.length > 0 ? 'info' : 'neutral'}
                    value={loadingAddresses ? 'Loading...' : `${destinationAddresses.length} saved`}
                    hint={
                      destinationAddresses.length === 0
                        ? 'None yet'
                        : destinationAddresses
                            .slice(0, 3)
                            .map((a: any) => a.label || addressText(a))
                            .join(' · ') +
                          (destinationAddresses.length > 3 ? ` · +${destinationAddresses.length - 3} more` : '')
                    }
                    actionLabel="Open addresses"
                    onClick={() => setActiveTab('addresses')}
                  />

                  <OverviewTile
                    icon={MapPinned}
                    title="Pickup Notes"
                    tone={account.pickup_notes ? 'info' : 'neutral'}
                    value={account.pickup_notes || 'None'}
                    hint="Default pickup instructions"
                    actionLabel="Edit notes"
                    onClick={() => editAt({ tab: 'traveller', section: 'transport' })}
                  />

                  <OverviewTile
                    icon={MapPinned}
                    title="Dropoff Notes"
                    tone={account.dropoff_notes ? 'info' : 'neutral'}
                    value={account.dropoff_notes || 'None'}
                    hint="Default dropoff instructions"
                    actionLabel="Edit notes"
                    onClick={() => editAt({ tab: 'traveller', section: 'transport' })}
                  />
                </TileSection>

                <TileSection icon={Settings} title="Miscellaneous">
                  <CRMLinkedRecordsPanel
                    accountId={account.account_id}
                    defaultCostCode={account.default_cost_code}
                    onEditCostCode={() => editAt({ tab: 'operational' })}
                  />

                  <OverviewTile
                    icon={ClipboardList}
                    title="Jobs"
                    value={`${jobCount} job${jobCount === 1 ? '' : 's'}`}
                    hint={`${overviewJobCount} in the last 7 days`}
                    actionLabel="View jobs"
                    onClick={() => setActiveTab('jobs')}
                  />

                  <OverviewTile
                    icon={Accessibility}
                    title="Mobility Notes"
                    tone={account.mobility_notes ? 'info' : 'neutral'}
                    value={account.mobility_notes || 'No mobility notes'}
                    hint={account.traveller_notes || undefined}
                    actionLabel="Edit notes"
                    onClick={() => editAt({ tab: 'traveller', section: 'transport' })}
                  />

                  <OverviewTile
                    icon={Settings}
                    title="Requirements"
                    tone={requirementLabels.length > 0 ? 'warn' : 'neutral'}
                    value={requirementLabels.length > 0 ? requirementLabels.join(' · ') : 'None'}
                    hint={requirementLabels.length > 0 ? 'Needed on bookings for this account' : 'No extra booking requirements'}
                    actionLabel="Edit requirements"
                    onClick={() => editAt({ tab: 'operational' })}
                  />
                </TileSection>
              </TabsContent>

              <TabsContent value="hierarchy" className="mt-0 space-y-4">
                {loadingHierarchy ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Loading hierarchy...
                  </div>
                ) : (
                  <>
                    {parentAccount && (
                      <Card className="bg-secondary/30 border-border">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4" />
                            Parent Account
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-3 bg-secondary/40 rounded-lg p-3 border border-border">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="text-foreground font-medium">{parentAccount.account_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{parentAccount.account_id}</p>
                            </div>
                          </div>

                          {account.use_parent_operational_defaults && (
                            <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              Inheriting operational defaults from parent
                            </p>
                          )}

                          {account.use_parent_billing_defaults && (
                            <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              Inheriting billing defaults from parent
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {billingAccount && (
                      <Card className="bg-secondary/30 border-border">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Billing Account
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-3 bg-secondary/40 rounded-lg p-3 border border-border">
                            <CreditCard className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="text-foreground font-medium">{billingAccount.account_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{billingAccount.account_id}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="bg-secondary/30 border-border">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Sub-Accounts
                          <Badge variant="secondary" className="text-xs">
                            {subAccounts.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {subAccounts.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No sub-accounts linked to this account.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {subAccounts.map((child) => (
                              <div
                                key={child.id}
                                className="flex items-center justify-between bg-secondary/40 rounded-lg p-3 border border-border"
                              >
                                <div className="flex items-center gap-3">
                                  <Building2 className="w-4 h-4 text-muted-foreground" />
                                  <div>
                                    <p className="text-foreground text-sm font-medium">{child.account_name}</p>
                                    <p className="text-xs text-muted-foreground font-mono">{child.account_id}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {child.account_category && (
                                    <Badge variant="outline" className="text-xs">
                                      {child.account_category}
                                    </Badge>
                                  )}
                                  <Badge
                                    className={
                                      child.is_active
                                        ? 'bg-green-900/50 text-green-400 border-green-700 text-xs'
                                        : 'bg-red-900/50 text-red-400 border-red-700 text-xs'
                                    }
                                  >
                                    {child.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/30 border-border">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <UserCircle className="w-4 h-4" />
                          Linked Travellers
                          <Badge variant="secondary" className="text-xs">
                            {travellers.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {travellers.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No traveller records linked to this account.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {travellers.map((t) => (
                              <div
                                key={t.id}
                                className="flex items-center justify-between bg-secondary/40 rounded-lg p-3 border border-border"
                              >
                                <div className="flex items-center gap-3">
                                  <UserCircle className="w-4 h-4 text-muted-foreground" />
                                  <div>
                                    <p className="text-foreground text-sm font-medium">
                                      {t.traveller_full_name || t.account_name}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <span className="font-mono">{t.account_id}</span>
                                      {t.traveller_phone && <span>{t.traveller_phone}</span>}
                                      {t.mobility_notes && (
                                        <Badge className="bg-purple-900/40 text-purple-400 border-purple-700/50 text-[10px]">
                                          <Accessibility className="w-2.5 h-2.5 mr-0.5" />
                                          Mobility
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {onCreateSubAccount && (
                      <Card className="bg-secondary/30 border-border">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Create Linked Account
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-xs text-muted-foreground mb-3">
                            Create a new account linked to{' '}
                            <span className="font-mono text-foreground">{account.account_id}</span> as a
                            sub-account, traveller, or out-of-area variant.
                          </p>

                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-600/60 text-blue-400 hover:bg-blue-900/30"
                              onClick={() =>
                                onCreateSubAccount(account.account_id, account.account_name, 'sub-account')
                              }
                            >
                              <Plus className="w-3.5 h-3.5 mr-1.5" />
                              Create Sub-Account
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-600/60 text-purple-400 hover:bg-purple-900/30"
                              onClick={() =>
                                onCreateSubAccount(account.account_id, account.account_name, 'traveller')
                              }
                            >
                              <UserCircle className="w-3.5 h-3.5 mr-1.5" />
                              Create Traveller
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-600/60 text-orange-400 hover:bg-orange-900/30"
                              onClick={() => onCreateSubAccount(account.account_id, account.account_name, 'ooa')}
                              disabled={childAccounts.some((c) => c.account_id.endsWith('-OoA'))}
                            >
                              <MapPin className="w-3.5 h-3.5 mr-1.5" />
                              Create OoA Account
                            </Button>
                          </div>

                          {childAccounts.some((c) => c.account_id.endsWith('-OoA')) && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-orange-400" />
                              <span>
                                An Out of Area account (
                                <span className="font-mono">{account.account_id}-OoA</span>) already exists
                                for this account.
                              </span>
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="billing" className="mt-0 space-y-4">
                <Card className="bg-secondary/30 border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Billing Details
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    {!account.billing_name &&
                    !account.billing_email &&
                    !account.billing_phone &&
                    !account.billing_address_line_1 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No billing details configured. Click Edit to add billing information.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <DetailRow label="Billing Name" value={account.billing_name} icon={Building2} />
                        <DetailRow label="Billing Contact" value={account.billing_contact_name} icon={User} />
                        <DetailRow label="Billing Email" value={account.billing_email} icon={Mail} />
                        <DetailRow label="Billing Phone" value={account.billing_phone} icon={Phone} />
                        <DetailRow label="Invoice Email" value={account.invoice_email} icon={Mail} />
                        <DetailRow label="AP Email" value={account.accounts_payable_email} icon={Mail} />
                        <DetailRow label="Attention" value={account.billing_attention} icon={User} />
                        <DetailRow label="Payment Terms" value={account.payment_terms} icon={CreditCard} />

                        <div className="sm:col-span-2">
                          <DetailRow
                            label="Billing Address"
                            value={[
                              account.billing_address_line_1,
                              account.billing_address_line_2,
                              account.billing_suburb,
                              account.billing_city,
                              account.billing_postcode,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                            icon={MapPin}
                          />
                        </div>

                        {account.billing_notes && (
                          <div className="sm:col-span-2">
                            <DetailRow label="Billing Notes" value={account.billing_notes} icon={FileText} />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="traveller" className="mt-0 space-y-4">
                <Card className="bg-secondary/30 border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <UserCircle className="w-4 h-4" />
                      Traveller / Passenger Details
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    {!account.traveller_first_name &&
                    !account.traveller_last_name &&
                    !account.traveller_phone ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No traveller details configured. Click Edit to add traveller information.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <DetailRow
                          label="Full Name"
                          value={
                            account.traveller_full_name ||
                            [account.traveller_first_name, account.traveller_last_name]
                              .filter(Boolean)
                              .join(' ')
                          }
                          icon={User}
                        />
                        <DetailRow label="Phone" value={account.traveller_phone} icon={Phone} />
                        <DetailRow label="Mobile" value={account.traveller_mobile} icon={Phone} />
                        <DetailRow label="Email" value={account.traveller_email} icon={Mail} />
                        <DetailRow label="External Ref" value={account.external_client_ref} icon={FileText} />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {(account.mobility_notes ||
                  account.pickup_notes ||
                  account.dropoff_notes ||
                  account.traveller_notes) && (
                  <Card className="bg-secondary/30 border-border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <MapPinned className="w-4 h-4" />
                        Transport Notes
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      {account.mobility_notes && (
                        <div className="bg-purple-900/20 border border-purple-700/40 rounded-lg p-3">
                          <p className="text-xs text-purple-400 font-medium flex items-center gap-1 mb-1">
                            <Accessibility className="w-3 h-3" /> Mobility Notes
                          </p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">
                            {account.mobility_notes}
                          </p>
                        </div>
                      )}

                      {account.pickup_notes && (
                        <div>
                          <p className="text-xs text-muted-foreground font-medium mb-1">Pickup Notes</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{account.pickup_notes}</p>
                        </div>
                      )}

                      {account.dropoff_notes && (
                        <div>
                          <p className="text-xs text-muted-foreground font-medium mb-1">Dropoff Notes</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{account.dropoff_notes}</p>
                        </div>
                      )}

                      {account.traveller_notes && (
                        <div>
                          <p className="text-xs text-muted-foreground font-medium mb-1">General Notes</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{account.traveller_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

              </TabsContent>

              <TabsContent value="addresses" className="mt-0 space-y-4">
                <CRMBookableAddresses accountId={account.account_id} openApproval={account.open_approval ?? false} />
              </TabsContent>

              <TabsContent value="approvals" className="mt-0 space-y-4">
                <CRMAccountApprovals
                  accountId={account.account_id}
                  accountName={account.account_name}
                  onCurrentChanged={reloadAccountShellData}
                />
              </TabsContent>

              <TabsContent value="perm-bookings" className="mt-0 space-y-4">
                <CRMAccountPermanentBookings
                  accountId={account.account_id}
                  accountName={account.account_name}
                />
              </TabsContent>

              <TabsContent value="jobs" className="mt-0 space-y-4">
                <Card className="bg-secondary/30 border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        Linked TaxiCaller Jobs
                        <Badge variant="secondary" className="text-xs">
                          {jobCount}
                        </Badge>
                      </CardTitle>

                      <div className="flex items-center gap-1">
                        <div className="flex items-center gap-0.5 bg-secondary/50 rounded-md p-0.5 border border-border">
                          {(['all', 'day', 'week', 'month'] as const).map((f) => (
                            <Button
                              key={f}
                              size="sm"
                              variant={jobDateFilter === f ? 'default' : 'ghost'}
                              className={`h-6 px-2.5 text-[11px] ${
                                jobDateFilter === f
                                  ? 'bg-yellow-600/80 text-white hover:bg-yellow-600'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                              onClick={() => {
                                setJobDateFilter(f);
                                setJobPage(0);
                              }}
                            >
                              {f === 'all' ? 'All' : f === 'day' ? 'Day' : f === 'week' ? 'Week' : 'Month'}
                            </Button>
                          ))}
                        </div>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={loadJobs}
                          disabled={loadingJobs}
                          className="text-muted-foreground hover:text-foreground h-7 w-7 p-0"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${loadingJobs ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    {loadingJobs && jobs.length === 0 ? (
                      <div className="flex items-center justify-center py-8 text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Loading jobs...
                      </div>
                    ) : jobs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No linked TaxiCaller jobs found for this account.
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-border">
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Job ID</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Date</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Time</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Passenger</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Pickup</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Dropoff</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Project</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Cost Code</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Reference</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Fleet</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Driver</TableHead>
                                <TableHead className="text-xs text-muted-foreground whitespace-nowrap">Vehicle</TableHead>
                              </TableRow>
                            </TableHeader>

                            <TableBody>
                              {jobs.map((job) => {
                                const passengerName = formatPassengerName(job.Passenger_First_Name, job.Passenger_Last_Name);

                                return (
                                  <TableRow key={job.id} className="border-border text-xs">
                                    <TableCell className="font-mono whitespace-nowrap">{job.Job_ID || '-'}</TableCell>
                                    <TableCell className="whitespace-nowrap">{job.pu_date || '-'}</TableCell>
                                    <TableCell className="whitespace-nowrap">
                                      {job.pu_time ? job.pu_time.substring(0, 5) : '-'}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap max-w-[120px] truncate">
                                      {passengerName || '-'}
                                    </TableCell>
                                    <TableCell className="max-w-[140px] truncate" title={job.Pick_Up_Location || ''}>
                                      {job.Pick_Up_Location || '-'}
                                    </TableCell>
                                    <TableCell className="max-w-[140px] truncate" title={job.Drop_Off_Location || ''}>
                                      {job.Drop_Off_Location || '-'}
                                    </TableCell>
                                    <TableCell>
                                      {job.Project ? (
                                        <span className="text-foreground">{job.Project}</span>
                                      ) : (
                                        <span className="text-red-400/60 italic">blank</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job.Cost_Code ? (
                                        <span className="text-foreground">{job.Cost_Code}</span>
                                      ) : (
                                        <span className="text-red-400/60 italic">blank</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job.Reference ? (
                                        <span className="text-foreground">{job.Reference}</span>
                                      ) : (
                                        <span className="text-red-400/60 italic">blank</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">{job.fleet || '-'}</TableCell>
                                    <TableCell className="whitespace-nowrap max-w-[100px] truncate">
                                      {job.driver_name || '-'}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">{job.vehicle_number || '-'}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        {totalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                            <span className="text-xs text-muted-foreground">
                              Page {jobPage + 1} of {totalPages} ({jobCount} total)
                            </span>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={jobPage === 0}
                                onClick={() => setJobPage((p) => Math.max(0, p - 1))}
                                className="text-muted-foreground"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={jobPage >= totalPages - 1}
                                onClick={() => setJobPage((p) => p + 1)}
                                className="text-muted-foreground"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="golden-text">Preview: Affected TaxiCaller Jobs</DialogTitle>
          </DialogHeader>

          {previewData ? (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <div className="bg-secondary/50 rounded-lg px-4 py-2">
                  <span className="text-muted-foreground">Total Linked:</span>{' '}
                  <span className="text-foreground font-semibold">{previewData.total_linked || 0}</span>
                </div>
                <div className="bg-yellow-900/30 rounded-lg px-4 py-2 border border-yellow-700/50">
                  <span className="text-yellow-400">Would be updated:</span>{' '}
                  <span className="text-yellow-300 font-semibold">{previewData.affected_count || 0}</span>
                </div>
              </div>

              {(previewData.affected_rows || []).length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <p>No jobs need backfilling. All fields are already populated.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead className="text-xs">Job ID</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Passenger</TableHead>
                        <TableHead className="text-xs">Project</TableHead>
                        <TableHead className="text-xs">Cost Code</TableHead>
                        <TableHead className="text-xs">Reference</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {(previewData.affected_rows || []).slice(0, 100).map((row: any) => {
                        const pName = formatPassengerName(row.Passenger_First_Name, row.Passenger_Last_Name);
                        const wouldFillProject =
                          (!row.Project || row.Project.trim() === '') && account?.default_project;
                        const wouldFillCost =
                          (!row.Cost_Code || row.Cost_Code.trim() === '') && account?.default_cost_code;
                        const wouldFillRef =
                          (!row.Reference || row.Reference.trim() === '') && account?.default_reference;

                        return (
                          <TableRow key={row.id} className="border-border text-xs">
                            <TableCell className="font-mono">{row.Job_ID || '-'}</TableCell>
                            <TableCell>{row.pu_date || '-'}</TableCell>
                            <TableCell>{pName || '-'}</TableCell>
                            <TableCell>
                              {wouldFillProject ? (
                                <span className="text-yellow-400">
                                  <AlertCircle className="w-3 h-3 inline mr-1" />
                                  {account?.default_project}
                                </span>
                              ) : (
                                <span className="text-foreground">{row.Project || '-'}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {wouldFillCost ? (
                                <span className="text-yellow-400">
                                  <AlertCircle className="w-3 h-3 inline mr-1" />
                                  {account?.default_cost_code}
                                </span>
                              ) : (
                                <span className="text-foreground">{row.Cost_Code || '-'}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {wouldFillRef ? (
                                <span className="text-yellow-400">
                                  <AlertCircle className="w-3 h-3 inline mr-1" />
                                  {account?.default_reference}
                                </span>
                              ) : (
                                <span className="text-foreground">{row.Reference || '-'}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {(previewData.affected_rows || []).length > 100 && (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Showing first 100 of {previewData.affected_count} affected rows.
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Fields highlighted in yellow would be filled from CRM defaults. Existing non-blank values will
                not be overwritten.
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading preview...
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              className="border-border text-muted-foreground"
            >
              Close
            </Button>

            {previewData && (previewData.affected_count || 0) > 0 && (
              <Button
                className="golden-button"
                onClick={() => {
                  setShowPreview(false);
                  handleBackfill();
                }}
                disabled={backfilling}
              >
                <Zap className="w-3.5 h-3.5 mr-1" />
                Run Backfill Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-foreground">
              Are you sure you want to permanently delete this account?
            </p>

            <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3 space-y-1">
              <p className="text-sm font-semibold text-foreground">{account.account_name}</p>
              <p className="text-xs text-muted-foreground font-mono">{account.account_id}</p>
              {account.billed_status && (
                <Badge className="bg-secondary text-muted-foreground border-border text-xs mt-1">
                  {account.billed_status}
                </Badge>
              )}
            </div>

            <div className="bg-yellow-900/20 border border-yellow-700/40 rounded-lg p-3">
              <p className="text-xs text-yellow-400 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  This will permanently remove the account and all associated addresses. Any linked TaxiCaller
                  jobs will retain their Account_ID but the CRM record will no longer exist. This action cannot
                  be undone.
                </span>
              </p>
            </div>

            {jobCount > 0 && (
              <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-3">
                <p className="text-xs text-blue-400 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    This account has <strong>{jobCount}</strong> linked TaxiCaller job
                    {jobCount !== 1 ? 's' : ''}. The jobs will not be deleted, but they will reference an
                    account that no longer exists.
                  </span>
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="border-border text-muted-foreground"
              disabled={deleting}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1.5" />
              )}
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CRMAccountDetail;